// The approach on the card (numbers.md §4.1, R2371, R2376;
// ui-map-interface.md §8 "The approach on the card").
//
// The parts the cards of `mapcards.ts` add (every world plays the approach,
// R2400):
//
//   - the signature order of an own hull: one press in the card body with
//     the order word alone, and beside it its state (● READY, ○ SPENT,
//     ACTIVE with the time left) over the recharge clock of a spent charge
//     (R2405). The
//     summary of the order (what it does, and the class default trigger
//     that fires while the commander is offline, R2387) is the hover title
//     of the press alone;
//   - the aspect word of a contact: the arc of the contact that faces the
//     reader's own hull, BOW, BEAM or STERN;
//   - the line of a spent order in the notices column.
//
// The server decides every aspect, spend and recharge. The client computes
// the aspect word for the reader alone, with the arc cosines of
// `CatalogRules.approach`, and a sentence reads every figure from there.
// The module is pure: it reads no state and writes no DOM.

import {
  HitAspect, SignatureCharge, SignatureOrder, SignatureTrigger,
  type ApproachRules, type CommandShipRules, type Event as WireEvent,
} from '../../gen/proto/aquaphobia';
import { card as S, overlay as SO } from '../../gen/strings';
import { esc } from '../esc';
import type { FleetApproach } from '../overlay/types';
import { buttonHtml, type Action } from './card';

/** The act of the order press: one `FLEET_OP_SIGNATURE` for the hull. */
export const SIG_ACT = 'signature';

/**
 * The order press would take a press now (R2376, R2395): the class has an
 * order, the charge is READY and no effect holds. The key `s` reads the
 * same gate as the press. `a` is absent on a frame with no approach state.
 */
export function signatureReady(order: SignatureOrder | undefined,
  a: Pick<FleetApproach, 'charge' | 'active'> | null | undefined): boolean {
  return !!a && order !== undefined && order !== SignatureOrder.SIGNATURE_ORDER_NONE
    && a.charge === SignatureCharge.SIGNATURE_CHARGE_READY && !a.active;
}

/** The word of one order on a label, or '' for none. */
export function orderWord(o: SignatureOrder): string {
  switch (o) {
    case SignatureOrder.SIGNATURE_ORDER_OUTFLANK: return S.sig_outflank;
    case SignatureOrder.SIGNATURE_ORDER_BRACE: return S.sig_brace;
    case SignatureOrder.SIGNATURE_ORDER_CANISTER: return S.sig_canister;
    case SignatureOrder.SIGNATURE_ORDER_DIG_IN: return S.sig_dig_in;
    case SignatureOrder.SIGNATURE_ORDER_BROADSIDE: return S.sig_broadside;
    case SignatureOrder.SIGNATURE_ORDER_CRASH_DIVE: return S.sig_crash_dive;
    case SignatureOrder.SIGNATURE_ORDER_MUSTER: return S.sig_muster;
    case SignatureOrder.SIGNATURE_ORDER_GETAWAY: return S.sig_getaway;
    default: return '';
  }
}

/** A share as a whole percent: 0.5 is 50. */
const pct = (share: number): number => Math.round(share * 100);

/** The word of the class default trigger, `ShipSpec.signature_trigger`
 *  of the catalog (R2387). BELOW reads its share off the rules. MANUAL
 *  and none fire nothing offline, so they have no word: ''. */
export function triggerWord(t: SignatureTrigger, r: ApproachRules | undefined): string {
  switch (t) {
    case SignatureTrigger.SIGNATURE_TRIGGER_OPENING_SHOT: return S.trig_opening_shot;
    case SignatureTrigger.SIGNATURE_TRIGGER_HIT_FIRST: return S.trig_hit_first;
    case SignatureTrigger.SIGNATURE_TRIGGER_BELOW_HALF: return S.trig_below_half({ pct: pct(r?.belowHullFrac ?? 0) });
    case SignatureTrigger.SIGNATURE_TRIGGER_FORMATION_TRAVEL: return S.trig_formation_travel;
    default: return '';
  }
}

/** A multiplier as the label writes it: `0.5`, `1.5`, `2`. */
const mul = (v: number): string => String(Math.round(v * 100) / 100);

/** The sentence of what one order does, with the figures of the rules.
 *  MUSTER reads the figures of the command ship, `CatalogRules.command`
 *  (R2628, R2805); with no figures it writes nothing. */
export function orderEffect(o: SignatureOrder, r: ApproachRules | undefined, c?: CommandShipRules): string {
  if (o === SignatureOrder.SIGNATURE_ORDER_MUSTER) {
    return c ? S.eff_muster({ px: c.musterReachPx, mul: mul(c.musterBoostMul) }) : '';
  }
  if (!r) return '';
  switch (o) {
    case SignatureOrder.SIGNATURE_ORDER_OUTFLANK:
      return S.eff_outflank({ sec: r.outflankSec, mul: mul(r.outflankSpeedMul) });
    case SignatureOrder.SIGNATURE_ORDER_BRACE:
      return S.eff_brace({ sec: r.braceSec, mul: mul(r.braceDamageTakenMul) });
    case SignatureOrder.SIGNATURE_ORDER_CANISTER: return S.eff_canister({ sec: r.canisterSec });
    case SignatureOrder.SIGNATURE_ORDER_DIG_IN: return S.eff_dig_in({ sec: r.digInSec, px: r.digInReachPx });
    case SignatureOrder.SIGNATURE_ORDER_BROADSIDE:
      return S.eff_broadside({ sec: r.broadsideSec, mul: mul(r.broadsideShotMul), splash: mul(r.broadsideSplashMul) });
    case SignatureOrder.SIGNATURE_ORDER_CRASH_DIVE:
      return S.eff_crash_dive({ sec: r.crashDiveSec, noise: mul(r.crashDiveNoiseMul), speed: mul(r.crashDiveSpeedMul) });
    case SignatureOrder.SIGNATURE_ORDER_GETAWAY:
      return S.eff_getaway({ sec: r.getawaySec, speed: mul(r.getawaySpeedMul), turn: mul(r.getawayTurnMul) });
    default: return '';
  }
}

// --- the own hull ------------------------------------------------------------

/**
 * The order press of an own hull (R2376, R2405): the order word alone.
 * READY takes the press; SPENT and ACTIVE stand disabled, and ACTIVE is
 * lit. The hover title is the summary of the order: what it does, and the
 * class default trigger that fires while the commander is offline (R2387).
 */
export function signatureAction(fleetId: number, order: SignatureOrder, a: FleetApproach,
  r: ApproachRules | undefined, classDefault: SignatureTrigger, c?: CommandShipRules): Action {
  const t = triggerWord(classDefault, r);
  const title = [orderEffect(order, r, c), t ? S.sig_offline({ trigger: t }) : ''].filter(Boolean).join(' ');
  const base: Action = { act: SIG_ACT, id: fleetId, face: 'quiet', label: orderWord(order), title: title || undefined };
  if (a.active) return { ...base, on: true, off: true };
  if (a.charge === SignatureCharge.SIGNATURE_CHARGE_READY) return base;
  return { ...base, off: true };
}

/**
 * The state of the order beside the press (R2405): ● READY, ○ SPENT, or
 * ACTIVE with `left`, the time left of an active order as the card prints
 * it ('' for MUSTER, which has no clock).
 */
export function signatureState(a: FleetApproach, left: string): string {
  if (a.active) return left ? S.sig_active({ left }) : S.sig_active_open;
  return a.charge === SignatureCharge.SIGNATURE_CHARGE_READY ? S.sig_charge_ready : S.sig_charge_spent;
}

/** The MAP seconds left on the recharge floor now (R2405): the frame's
 *  figure less the map seconds since, at `tacticalScale` map seconds a
 *  wall second; 0 once it has run out. A held floor (CANISTER or
 *  BROADSIDE while the effect holds, R2412) stands at the frame's figure. */
export function floorLeftSec(a: Pick<FleetApproach, 'floorSec' | 'floorAtMs' | 'floorHeld'>, tacticalScale: number, nowMs: number): number {
  if (!(a.floorSec > 0)) return 0;
  if (a.floorHeld) return a.floorSec;
  const scale = tacticalScale > 0 ? tacticalScale : 1;
  return Math.max(0, a.floorSec - ((nowMs - a.floorAtMs) / 1000) * scale);
}

/**
 * The recharge line of a spent charge (R2405): the floor left as the card
 * prints it, `floorLeft`, while it runs, then the wait for the regen gate
 * and a full shield. A READY charge has no line: ''.
 */
export function signatureRecharge(a: FleetApproach, floorLeft: string): string {
  if (a.charge !== SignatureCharge.SIGNATURE_CHARGE_SPENT) return '';
  return floorLeft ? S.sig_recharge({ dur: floorLeft }) : S.sig_recharge_wait;
}

/**
 * The row of the order (R2405, R2415): the press, and beside it the state
 * over the recharge line of `signatureRecharge`. The press keeps the quiet
 * face of the order row and its 44 px finger target, and shows the cap of
 * `action.key`, the scan key that spends the order (R2395); a touch screen
 * hides the cap. The closed context sheet of a telephone draws this row
 * alone (`CardSpec.peek`, R2415).
 */
export function signatureRowHtml(action: Action, state: string, recharge: string): string {
  return `<div class="ck-sigr">${buttonHtml(action, { keycaps: action.key !== undefined })}`
    + `<span class="ck-sigs"><b>${esc(state)}</b>`
    + (recharge ? `<i>${esc(recharge)}</i>` : '')
    + '</span></div>';
}

/** The body section of the order (R2376, R2396, R2405): a row of
 *  `signatureRowHtml` in a section with no cap head. */
export function signatureSection(row: string): string {
  return `<div class="ck-sect ck-sig">${row}</div>`;
}

/**
 * Why a long press on the slot of the selected hull does not spend its
 * order (R2415), as the slot toast states it, or '' when the order fires.
 * `name` names the hull, `order` is the order of its class, `a` the
 * approach state of the frame, `left` the time left of an active order as
 * the card prints it, and `docked` marks a hull in the pens. A class with
 * no order, and a frame with no charge, read as no order.
 */
export function signatureRefusal(name: string, order: SignatureOrder | undefined,
  a: FleetApproach | null | undefined, left: string, docked = false): string {
  const word = order === undefined ? '' : orderWord(order);
  if (!word) return SO.slot_sig_none({ name });
  if (docked) return SO.slot_sig_docked({ order: word });
  if (!a || a.charge === SignatureCharge.SIGNATURE_CHARGE_NONE) return SO.slot_sig_none({ name });
  if (signatureReady(order, a)) return '';
  return SO.slot_sig_state({ order: word, state: signatureState(a, left) });
}

// --- a contact ----------------------------------------------------------------

/** A point on the map. */
export interface Pt { x: number; y: number }

/**
 * The own hull the aspect word reads from: the selected own hull, else the
 * own hull nearest to the contact, else null (the reader has none at sea).
 */
export function aspectRef(own: readonly (Pt & { id: number })[], selectedId: number, at: Pt): Pt | null {
  const sel = selectedId ? own.find((f) => f.id === selectedId) : undefined;
  if (sel) return sel;
  let best: Pt | null = null;
  let bestD = Infinity;
  for (const f of own) {
    const d = (f.x - at.x) ** 2 + (f.y - at.y) ** 2;
    if (d < bestD) { bestD = d; best = f; }
  }
  return best;
}

/**
 * The arc of a hull at `at` with the bow heading `bowMrad` that faces the
 * point `from` (numbers.md §4.1): the cosine of the bearing from the hull
 * to the point against the bow. At or above `bow_cos` it is the bow, at or
 * below minus `stern_cos` the stern, else the beam. The pack states both
 * cosines as positive figures; the sign of the stern test is the rule's
 * own, so a pack that states it negative reads the same. A point on the
 * hull itself has no bearing: NONE.
 */
export function aspectOf(bowMrad: number, at: Pt, from: Pt, r: Pick<ApproachRules, 'bowCos' | 'sternCos'>): HitAspect {
  const dx = from.x - at.x, dy = from.y - at.y;
  const len = Math.hypot(dx, dy);
  if (!(len > 0)) return HitAspect.HIT_ASPECT_NONE;
  const h = bowMrad / 1000;
  const cos = (Math.cos(h) * dx + Math.sin(h) * dy) / len;
  if (cos >= r.bowCos) return HitAspect.HIT_ASPECT_BOW;
  if (cos <= -Math.abs(r.sternCos)) return HitAspect.HIT_ASPECT_STERN;
  return HitAspect.HIT_ASPECT_BEAM;
}

/** The label word of an aspect, or '' for none. */
export function aspectWord(a: HitAspect): string {
  switch (a) {
    case HitAspect.HIT_ASPECT_BOW: return S.aspect_bow;
    case HitAspect.HIT_ASPECT_BEAM: return S.aspect_beam;
    case HitAspect.HIT_ASPECT_STERN: return S.aspect_stern;
    default: return '';
  }
}

/** The charge of a contact as a fact value: the order word when the class
 *  is known, with ● READY or ○ SPENT; '' when the frame sent none. */
export function chargeText(charge: SignatureCharge, order: SignatureOrder): string {
  const word = orderWord(order);
  if (charge === SignatureCharge.SIGNATURE_CHARGE_READY) return word ? S.sig_ready({ order: word }) : S.sig_charge_ready;
  if (charge === SignatureCharge.SIGNATURE_CHARGE_SPENT) return word ? S.sig_spent({ order: word }) : S.sig_charge_spent;
  return '';
}

// --- the event line -------------------------------------------------------------

/** The class name of the one class whose order this is, from the catalog
 *  ships, or '' when the catalog names none. */
export function classOfOrder(ships: readonly { name: string; signature: SignatureOrder }[], o: SignatureOrder): string {
  if (o === SignatureOrder.SIGNATURE_ORDER_NONE) return '';
  return ships.find((sh) => sh.signature === o)?.name ?? '';
}

/** What the line of a spent order reads of the hull that spent it. */
export interface SigHull {
  own: boolean;
  /** The hull is of the reader's faction (own or a faction mate). */
  friend: boolean;
  /** The hull name the frame carried, or ''. */
  name: string;
}

/**
 * The line of one SIGNATURE event in the notices column (R2376): the hull
 * and the order, `Harrier · BRACE`. A hull of another faction reads by its
 * class, which the order names (each order belongs to one class), so the
 * line gives away nothing the event did not. `classOf` answers the class
 * name of an order from the catalog. Null for every other event.
 */
export function signatureLine(e: WireEvent, hull: SigHull | null, classOf: (o: SignatureOrder) => string): string | null {
  const order = orderWord(e.signature);
  if (!order) return null;
  const cls = classOf(e.signature) || S.contact_title;
  if (hull?.friend && e.signature === SignatureOrder.SIGNATURE_ORDER_GETAWAY) {
    return S.sig_line_getaway({ name: hull.name || cls, order }); // the cargo is lost (R2857)
  }
  if (hull?.friend) return S.sig_line({ name: hull.name || cls, order });
  return S.sig_line_hostile({ cls, order });
}
